import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import {
  useResolveReviewCardSnapshot,
  useResolveSentenceCardSnapshot,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-action";
import {
  type LearnCardControls,
  useRegisterLearnCardControls,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { DrillPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useReviewOnKeyPress } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.keypress";
import { useRevealMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import { useMemo } from "react";
import { useDrillPageResetAnswerInputOnCharacterChange } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reset-answer-input";

/**
 * What the CURRENT review card lends the deck: how to freeze itself into an action snapshot at
 * commit time (a grapheme drill resolves its FSRS card fresh; a sentence drill captures its state
 * and per-word cards), and the rating shortcut keys, which work from either face.
 */
export function DrillFlashcardControls() {
  // Controls stay mounted for the logical current presentation, including tab blur. Reset once
  // when that presentation becomes current, never merely because a face reacquires its view.
  useDrillPageResetAnswerInputOnCharacterChange();
  const { mutate: reveal } = useRevealMutation();
  const [character, drillType, sentenceDrill] = useContextSelectors(
    DrillPageContext,
    (c) => c.character,
    (c) => c.drillType,
    (c) => c.sentenceDrill,
  );
  const resolveReviewCardSnapshot = useResolveReviewCardSnapshot();
  const resolveSentenceCardSnapshot = useResolveSentenceCardSnapshot();
  const sentenceStateRef = useLatest(sentenceDrill === null ? null : sentenceDrill.state);
  const isSentence = sentenceDrill !== null;

  const controls = useMemo<LearnCardControls>(
    () => ({
      getActionSnapshot: () => {
        const sentenceState = sentenceStateRef.current;
        if (isSentence && sentenceState !== null) {
          return resolveSentenceCardSnapshot({ drillType, state: sentenceState, word: character });
        }
        return resolveReviewCardSnapshot({ drillType, grapheme: character });
      },
      kind: "review",
      reveal: () => reveal(),
    }),
    [
      character,
      reveal,
      drillType,
      isSentence,
      resolveReviewCardSnapshot,
      resolveSentenceCardSnapshot,
      sentenceStateRef,
    ],
  );
  useRegisterLearnCardControls(controls);

  const isFocused = useIsFocused();
  const isFocusedRef = useLatest(isFocused);
  useReviewOnKeyPress({ isFocusedRef });

  return null;
}
